import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, ChevronRight, CalendarDays } from 'lucide-react';
import logo from "../../assets/smartedzmain.png";

/* ---------------- NAV LINKS ----------------
   Plain links. To give any item a menu, add `dropdown: [{ title, path }]`.
   NOTE: paths are lowercase — keep your <Route path> values lowercase too. */
const NAV_LINKS = [
  { name: 'Home', path: '/' },
  { name: 'Products', path: '/products' },
  { name: 'About Us', path: '/about-us' },
  { name: 'Services', path: '/services' },
  { name: 'Industries', path: '/industries' },
  { name: 'Solutions', path: '/solutions' },
  { name: 'Technologies', path: '/technologies' },
  { name: 'Contact Us', path: '/contact-us' },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);                    // mobile drawer
  const [activeDropdown, setActiveDropdown] = useState(null); // desktop hover menu
  const [mobileSection, setMobileSection] = useState(null);   // mobile accordion

  const location = useLocation();
  const isDemo = location.pathname === '/request-demo';

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [open]);

  useEffect(() => { setOpen(false); setMobileSection(null); }, [location.pathname]);

  const container = "w-full max-w-[1600px] 2xl:max-w-[90vw] mx-auto px-4 sm:px-6 lg:px-8";

  const DemoButton = ({ full = false, onClick }) => (
    <Link
      to="/request-demo"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold text-white transition-all duration-300 ${
        full ? 'w-full px-5 py-3 text-base' : 'px-4 2xl:px-5 py-2.5 text-sm'
      } ${
        isDemo
          ? 'bg-[#f29132] shadow-lg shadow-[#f29132]/40 ring-2 ring-[#f29132]/30 2xl:scale-[1.04]'
          : 'bg-primary shadow-lg shadow-primary/30 hover:brightness-110 hover:-translate-y-0.5'
      }`}
    >
      <CalendarDays size={18} />
      Book a Demo
    </Link>
  );

  return (
    <>
      {/* keyframes (global) */}
      <style>{`
        @keyframes navSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes navFadeIn { from { opacity: 0; } to { opacity: 1; } }
        .nav-drawer { animation: navSlideIn .3s cubic-bezier(.4,0,.2,1); }
        .nav-backdrop { animation: navFadeIn .25s ease-out; }
      `}</style>

      <header className="sticky top-0 w-full bg-background-light/95 backdrop-blur border-b border-gray-200 z-50">
        <div className={container}>
          <div className="flex items-center justify-between h-[68px] sm:h-20 xl:h-24 gap-3">

            {/* LOGO */}
            <Link to="/" className="flex items-center shrink-0 group">
              <img src={logo} alt="SmartEdz Logo" className="h-10 sm:h-12 xl:h-16 w-auto object-contain transition-transform group-hover:scale-105" />
              <h2
                className="text-lg sm:text-xl xl:text-3xl font-bold tracking-tight leading-none ml-2 sm:ml-3"
                style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
              >
                <span className="bg-gradient-to-t from-[#18469A] via-[#1B83C5] to-[#21BEE1] bg-clip-text text-transparent">Smart</span><span className="bg-gradient-to-t from-[#E35D14] to-[#F4A51D] bg-clip-text text-transparent">Edz</span>
              </h2>
            </Link>

            {/* DESKTOP NAV — xl (1280px) and up */}
            <nav className="hidden xl:flex items-center gap-6 2xl:gap-8">
              {NAV_LINKS.map((item) => (
                <div
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.dropdown && setActiveDropdown(item.name)}
                  onMouseLeave={() => item.dropdown && setActiveDropdown(null)}
                >
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    className={({ isActive }) =>
                      `relative flex items-center gap-1 text-[15px] font-semibold whitespace-nowrap transition-colors ${
                        isActive ? "text-primary" : "text-gray-700"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className="relative py-1.5">
                          {item.name}
                          <span
                            className={`absolute left-0 -bottom-0.5 h-[2.5px] rounded-full transition-all duration-300 ease-out ${
                              isActive ? 'w-full' : 'w-0'
                            }`}
                            style={{ backgroundColor: '#f26522' }}
                          />
                        </span>
                        {item.dropdown && (
                          <ChevronDown size={14} className={`transition-transform duration-200 ${activeDropdown === item.name ? 'rotate-180' : ''}`} />
                        )}
                      </>
                    )}
                  </NavLink>

                  {item.dropdown && activeDropdown === item.name && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50">
                      <div className="w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2">
                        {item.dropdown.map((sub) => (
                          <NavLink key={sub.title} to={sub.path} className="block px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-primary/5 hover:text-primary transition-colors">
                            {sub.title}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            {/* RIGHT */}
            <div className="flex items-center gap-2 shrink-0">
              <div className="hidden xl:block">
                <DemoButton />
              </div>
              <button
                onClick={() => setOpen(true)}
                className="xl:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                aria-label="Open menu"
              >
                <Menu size={26} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE / TABLET DRAWER — rendered OUTSIDE the header so `fixed` fills the whole viewport
          (a backdrop-filter ancestor would otherwise trap fixed children inside it). */}
      {open && (
        <div className="nav-backdrop fixed inset-0 z-[100] bg-black/40 xl:hidden" onClick={() => setOpen(false)}>
          <aside
            className="nav-drawer absolute right-0 top-0 h-full w-[86%] max-w-sm bg-white shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center px-5 h-[68px] border-b border-gray-200 shrink-0">
              <span className="text-xl font-bold">
                <span className="bg-gradient-to-t from-[#18469A] to-[#21BEE1] bg-clip-text text-transparent">Smart</span><span className="bg-gradient-to-t from-[#E35D14] to-[#F4A51D] bg-clip-text text-transparent">Edz</span>
              </span>
              <button onClick={() => setOpen(false)} className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-[#f26522] transition" aria-label="Close menu">
                <X size={24} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-3">
              {NAV_LINKS.map((item) => (
                item.dropdown ? (
                  <div key={item.name} className="border-b border-gray-100">
                    <button
                      onClick={() => setMobileSection(mobileSection === item.name ? null : item.name)}
                      className="w-full flex items-center justify-between py-3.5 px-2 text-[15px] font-semibold text-gray-800"
                    >
                      {item.name}
                      <ChevronDown size={18} className={`text-gray-400 transition-transform duration-200 ${mobileSection === item.name ? 'rotate-180' : ''}`} />
                    </button>
                    {mobileSection === item.name && (
                      <div className="ml-2 mb-2 flex flex-col gap-1 border-l-2 border-primary/20 pl-4">
                        {item.dropdown.map((sub) => (
                          <NavLink key={sub.title} to={sub.path} onClick={() => setOpen(false)} className="py-2 text-sm text-gray-600 hover:text-primary">
                            {sub.title}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.name}
                    to={item.path}
                    end={item.path === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center justify-between py-3.5 px-2 text-[15px] font-semibold border-b border-gray-100 ${
                        isActive ? "text-primary" : "text-gray-800"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <span className={isActive ? 'border-l-[3px] border-[#f26522] -ml-2 pl-2' : ''}>{item.name}</span>
                        <ChevronRight size={16} className="text-gray-300" />
                      </>
                    )}
                  </NavLink>
                )
              ))}
            </nav>

            <div className="p-5 border-t border-gray-200 shrink-0">
              <DemoButton full onClick={() => setOpen(false)} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default Navbar;